import { useId, useRef, useState } from 'react';
import {
  CHINA_PAYMENT_MIN_TOTAL_RM_CENTS,
  chargeAmountProblems,
  parseRmbAmount,
  type ChargeLimits,
  type Quote,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import { VerificationNotice } from '@/features/account/index.js';
import { QuoteSummary } from '@/features/china-payment/components/QuoteSummary.js';
import type { QuoteState } from '@/features/china-payment/useQuote.js';
import { useFormat } from '@/lib/format.js';
import { actionLinkClass, buttonClass, inputClass } from '@/lib/styles.js';

export interface AmountStepProps {
  amount: string;
  onAmountChange: (amount: string) => void;
  /** The live quote for `amount` — idle while the amount is over `limits.maxRmbCents`. */
  quote: QuoteState;
  limits: ChargeLimits;
  /** Whether the account may send a request; otherwise the verification notice replaces Next. */
  verified: boolean;
  /** The same form on mypoz.com, for an amount the new app does not take. */
  oldSiteUrl: string;
  /** With the settled quote the next steps keep. */
  onNext: (quote: Quote) => void;
}

/**
 * Step 1 of a China Payment request (MYP-60, MYP-109): the RMB amount and its live quote — rate,
 * service fee and RM total, all the server's (`alipay_rechange`).
 *
 * Next needs a settled quote at or above the old website's RM 100 minimum. An amount above what the
 * app takes for the type (`maxRmbCents`, where the vendor's quote is wrong) gets no quote at all —
 * a wrong total is never shown — and a link to the same form on mypoz.com instead.
 */
export function AmountStep({
  amount,
  onAmountChange,
  quote,
  limits,
  verified,
  oldSiteUrl,
  onNext,
}: AmountStepProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const amountId = useId();
  const messageId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [tried, setTried] = useState(false);

  const rmbCents = parseRmbAmount(amount);
  const settled = quote.status === 'ready' && !quote.stale ? quote.quote : undefined;
  const problems = chargeAmountProblems(
    {
      ...(rmbCents !== undefined ? { rmbCents } : {}),
      ...(settled ? { totalRmCents: settled.totalRmCents } : {}),
    },
    limits,
  );
  const overLimit = problems.includes('overLimit');
  const belowMinimum = problems.includes('minimum');
  const invalid = quote.status === 'invalid' || (tried && problems.includes('amount'));
  const minimum = fmt.money(CHINA_PAYMENT_MIN_TOTAL_RM_CENTS, 'RM');

  const next = () => {
    setTried(true);
    if (problems.length > 0 || !settled) {
      if (!problems.includes('quote')) input.current?.focus();
      return;
    }
    onNext(settled);
  };

  return (
    <div className="grid gap-4">
      <div>
        <label htmlFor={amountId} className="mb-1.5 block text-sm text-muted">
          {t('chinaPayment.request.amountLabel')}
        </label>
        <input
          ref={input}
          id={amountId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          // Zero in the reader's own number format ("0.00", or "0,00" where that is the norm).
          placeholder={fmt.number(0, 2)}
          value={amount}
          onChange={(event) => onAmountChange(event.target.value)}
          aria-invalid={invalid || overLimit || belowMinimum || undefined}
          aria-describedby={invalid || overLimit || belowMinimum ? messageId : undefined}
          className={inputClass('tabular-nums')}
        />
        {invalid ? (
          <p id={messageId} className="mt-1.5 text-xs text-danger">
            {quote.status === 'invalid'
              ? t('chinaPayment.request.amountInvalid')
              : t('chinaPayment.form.problem.amount')}
          </p>
        ) : null}
      </div>

      {overLimit && limits.maxRmbCents !== undefined ? (
        <div
          id={messageId}
          className="flex items-start gap-3 rounded-lg border border-line bg-surface-alt p-4"
        >
          <Icon name="info" size={20} className="shrink-0 text-muted" />
          <div className="text-sm text-ink">
            <p>
              {t('chinaPayment.request.overLimit', {
                amount: fmt.money(limits.maxRmbCents, 'RMB'),
              })}
            </p>
            <a
              href={oldSiteUrl}
              target="_blank"
              rel="noreferrer"
              className={actionLinkClass('mt-1 inline-flex text-sm')}
            >
              {t('chinaPayment.newRequest.goToMypoz')}
            </a>
          </div>
        </div>
      ) : (
        <div>
          <QuoteSummary state={quote} />
          {belowMinimum ? (
            <p
              id={messageId}
              className={tried ? 'mt-2 text-xs text-danger' : 'mt-2 text-xs text-ink'}
            >
              {t('chinaPayment.form.problem.minimum', { amount: minimum })}
            </p>
          ) : null}
        </div>
      )}

      {verified ? (
        <div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={next}
              disabled={overLimit}
              className={buttonClass(
                'primary',
                'w-full px-6 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto',
              )}
            >
              {t('chinaPayment.form.next')}
              <Icon name="arrow_forward" size={18} />
            </button>
          </div>
          {tried && problems.includes('quote') ? (
            <p role="alert" className="mt-2 text-right text-xs text-danger">
              {quote.status === 'error'
                ? t('chinaPayment.request.quoteFailed')
                : t('chinaPayment.form.problem.quote')}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="border-t border-line pt-4">
          <VerificationNotice
            heading="h3"
            title={t('chinaPayment.form.verify.title')}
            body={t('chinaPayment.form.verify.body')}
            finalStep={t('chinaPayment.form.verify.step3')}
          />
        </div>
      )}
    </div>
  );
}
